import { useState } from 'react';
import type { EventoAgenda, EventoAtual, EventoTabela } from '../../api/evento';
import { numero, t } from '../../i18n/idioma';
import { nomeDoItem } from '../../i18n/nomes';
import { ItemIcon } from '../ItemIcon';
import { Selo } from './Selo';
import { formatar, formatarLongo, restanteDe } from './tempo';
import { TIPO_UB, aoVivo, proximoDo, quandoDe, quandoTexto } from './tipos';

export type AbaUb = 'recompensas';

interface Props {
  atual: EventoAtual | null;
  agenda: EventoAgenda | null;
  /** O 'como' antigo cai em Recompensas: o Como funciona saiu — o detalhe fica na Wiki (como no Chefe do Mundo). */
  abaInicial: AbaUb | 'como';
  busy: boolean;
  razao: string | null;
  moeda: string;
  onInscrever(): void;
  onDesistir(): void;
}

/**
 * O detalhe do Ultimate Battle em Mais › Eventos (techspec-eventos §09), no molde do Chefe do Mundo: cabeçalho compacto,
 * a ação do momento e as recompensas. O prêmio do sorteio chega pelo correio (techspec-correio §02): a aba Prêmios
 * saiu. Regras, pódio e traje ficam na Wiki.
 */
export function UltimateBattle(p: Props) {
  const { atual, agenda } = p;
  const porSala = atual?.porSala ?? agenda?.porSala ?? 6;
  return (
    <>
      <div className="ev-heroi ev-heroi--compacto">
        <Selo id="ub" />
        <div>
          <h3>{t('evento.nome')}</h3>
          <p>{t('evento.ub.resumo', { n: porSala })}</p>
        </div>
      </div>
      <Acao {...p} />
      <div className="ev-aba ev-aba--compacta">
        <Recompensas agenda={agenda} />
        <p className="ev-dica ev-dica--correio">{t('evento.premio.correio')}</p>
      </div>
    </>
  );
}

function Acao({ atual, agenda, busy, razao, moeda, onInscrever, onDesistir }: Props) {
  const vivo = aoVivo(atual);
  const minimo = atual?.minimo ?? agenda?.minimo ?? 2;
  const porSala = atual?.porSala ?? agenda?.porSala ?? 6;
  if (vivo === 'luta' && atual) {
    const daemon = Math.round(((atual.partidaSegundos || 900) - (atual.daemonSegundos || 180)) / 60);
    return (
      <div className="ev-acao">
        <div className="ev-acao__l">
          <span className="ev-vivo">{t('evento.quadro.aoVivo')}</span>
          <span className="ev-pips">{Array.from({ length: porSala }, (_, i) => <i key={i} className={i < atual.vivos ? '' : 'is-off'} />)}</span>
        </div>
        <div className="ev-acao__l">
          <small>{t('evento.det.luta', { n: atual.vivos, d: daemon })}</small>
          <span className="ev-relogio">{formatar(restanteDe(atual.fimEmMs))}</span>
        </div>
      </div>
    );
  }
  if (vivo === 'inscricao' && atual) {
    return (
      <div className="ev-acao">
        <div className="ev-acao__l">
          <span className="ev-vivo">{t('evento.quadro.inscricoes')}</span>
          <span className="ev-relogio">{formatar(restanteDe(atual.fechaEmMs))}</span>
        </div>
        {atual.inscrito ? (
          <div className="ev-acao__l">
            <small>{t('evento.det.inscrito', { n: atual.inscritos })}</small>
            <button type="button" className="btn btn--mini" disabled={busy} onClick={onDesistir}>{t('evento.desistir')}</button>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn--primary ev-cheio" disabled={busy || !!razao} onClick={onInscrever}>
              {t('evento.inscreverMoeda', { n: numero(atual.taxa), moeda })}
            </button>
            {razao
              ? <p className="ev-dica ev-dica--aviso">{razao}</p>
              : <p className="ev-dica">{t('evento.det.aberto', { n: atual.inscritos, min: minimo })} {t('evento.conselhoCofre')}</p>}
          </>
        )}
      </div>
    );
  }
  const prox = proximoDo(TIPO_UB, agenda);
  const inscricaoMin = Math.round((agenda?.inscricaoSegundos || atual?.inscricaoSegundos || 300) / 60);
  const avisoMin = Math.round((agenda?.avisoSegundos || 900) / 60);
  const taxa = agenda?.inscricao ?? atual?.taxa ?? 0;
  return (
    <>
      <div className="ev-acao ev-acao--linha" title={t('evento.det.dicaAgenda', { n: inscricaoMin, a: avisoMin })}>
        <small>{t('evento.det.proximo')}</small>
        {prox ? (
          <b className="ev-acao__quando">
            {quandoTexto(prox, 'frase')}
            {quandoDe(prox) === 'hoje' && <span> · {formatarLongo(restanteDe(prox.emMs))}</span>}
          </b>
        ) : <b className="ev-acao__quando">{t('evento.chip.semHorario')}</b>}
      </div>
      {taxa > 0 && <p className="ev-dica">{t('evento.ub.inscricao', { n: numero(taxa), moeda, min: inscricaoMin })}</p>}
    </>
  );
}

const pct = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

function Recompensas({ agenda }: { agenda: EventoAgenda | null }) {
  const tabelas = agenda?.tabelas ?? [];
  const [id, setId] = useState<string | null>(null);
  const tabela = tabelas.find(x => x.id === id) ?? tabelas[0] ?? null;
  if (!tabela) return <p className="ev-vazio">{t('evento.drop.vazio')}</p>;
  const participacao = tabela === tabelas[0];
  return (
    <>
      {tabelas.length > 1 && (
        <div className="ev-seg" role="group">
          {tabelas.map(x => (
            <button key={x.id} type="button" aria-pressed={x.id === tabela.id} onClick={() => setId(x.id)}>{x.nome || x.id}</button>
          ))}
        </div>
      )}
      <p className="ev-dica">
        {participacao ? t('evento.recomp.participacao') : t('evento.recomp.vitoria')} {t('evento.recomp.saiu')}
      </p>
      <Tabela tabela={tabela} />
      {agenda && tabela.garantiaEventos > 0 && (
        <Garantia n={Math.min(agenda.garantiaContador, tabela.garantiaEventos)} de={tabela.garantiaEventos} raridade={tabela.garantiaRaridade ?? 'raro'} />
      )}
      {agenda && <p className="ev-dica">{t('evento.sorteiosDia', { n: agenda.sorteiosHoje, de: agenda.sorteiosPorDia })}</p>}
    </>
  );
}

function Tabela({ tabela }: { tabela: EventoTabela }) {
  return (
    <div className="ev-rars ev-rars--compacto">
      {tabela.raridades.map(r => (
        <div key={r.id} className={`ev-rar ev-rar--${r.id}`}>
          <div className="ev-rar__h"><b>{t(`evento.raridade.${r.id}`)}</b><span>{pct(r.chance)}</span></div>
          <div className="ev-itens">
            {r.itens.map(i => {
              const q = i.qtdMin === i.qtdMax ? numero(i.qtdMin) : `${numero(i.qtdMin)}–${numero(i.qtdMax)}`;
              return (
                <span key={`${r.id}-${i.def}`} className="ev-item" title={`${nomeDoItem(i.def)} ×${q} · ${pct(i.chance)}`}>
                  <ItemIcon id={i.def} size={30} quantity={i.qtdMax} label={`${nomeDoItem(i.def)} ×${q} · ${pct(i.chance)}`} />
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Garantia({ n, de, raridade }: { n: number; de: number; raridade: string }) {
  const r = t(`evento.raridade.${raridade}`);
  return (
    <div className="ev-garantia">
      <div className="ev-garantia__l"><span>{t('evento.garantia.titulo', { r })}</span><span className="ev-mono">{n}/{de}</span></div>
      <span className="ev-garantia__bar">{Array.from({ length: de }, (_, i) => <i key={i} className={i < n ? 'is-on' : ''} />)}</span>
      <p className="ev-dica">{t('evento.garantia.falta', { n: Math.max(0, de - n), r })}</p>
    </div>
  );
}
